import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Sun, Palmtree, Building2, Plane, ExternalLink, Sparkles, Coffee, Shield, CheckCircle2 } from 'lucide-react';
import { TripModal } from './TripModal';

const TRIP_ID = 'collaboration';

const campusImages = [
  {
    id: 'nice-promenade',
    url: 'https://images.unsplash.com/photo-1643914729809-4aa59fdc4c17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxOaWNlJTIwRnJhbmNlJTIwcHJvbWVuYWRlJTIwZGVzJTIwYW5nbGFpcyUyMHN1bm55fGVufDB8fHx8MTc3MDU0MTU0N3ww&ixlib=rb-4.1.0&q=85',
    alt: 'Nice Promenade',
    span: 'col-span-2 row-span-2',
    url_link: 'https://www.gettyimages.fr/photos/nice',
  },
  {
    id: 'graduation',
    url: 'https://customer-assets.emergentagent.com/job_2af900fb-a4c0-4108-9990-fef980765d9c/artifacts/jyeb5rxg_Cambridge%202018.jpg',
    alt: 'Cérémonie de remise des diplômes',
    span: 'col-span-1 row-span-2',
  },
  {
    id: 'collaboration',
    url: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&auto=format&fit=crop',
    alt: 'Photos voyages',
    span: 'col-span-1 row-span-2',
    url_link: 'https://www.collegelyceelafayette.com/london-trip',
  },
];

const highlightsConfig = [
  { key: 'location', icon: MapPin, url: 'https://www.explorenicecotedazur.com' },
  { key: 'sun', icon: Sun, url: 'https://meteofrance.com/previsions-meteo-france/nice/06000' },
  { key: 'mediterranean', icon: Palmtree, url: 'https://provence-alpes-cotedazur.com/provence/decouvrir/art-de-vivre/' },
  { key: 'modern', icon: Building2 },
];

export const Campus = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [tripOpen, setTripOpen] = useState(false);
  const openTrip = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setTripOpen(true);
  };

  return (
    <section id="campus" data-testid="campus-section" ref={ref} className="py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-amber-600 font-inter text-sm font-semibold tracking-wider uppercase">
            {t('campus.tag')}
          </span>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 mb-6">
            {t('campus.title1')} <span className="italic">{t('campus.title1Italic')}</span> {t('campus.title2')}
          </h2>
          <div className="section-divider mb-6" />
          <p className="font-inter text-slate-600 text-lg max-w-2xl mx-auto">
            {t('campus.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
        >
          {highlightsConfig.map((item) => {
            const Tag = item.url ? 'a' : 'div';
            const tagProps = item.url
              ? { href: item.url, target: '_blank', rel: 'noopener noreferrer' }
              : {};
            return (
              <Tag
                key={item.key}
                {...tagProps}
                {...(item.url ? { title: t('common.opensInNewTab') } : {})}
                data-testid={`campus-highlight-${item.key}`}
                className={`bg-white rounded-xl p-5 border border-slate-200 text-center card-hover relative block ${
                  item.url ? 'hover:border-amber-300 cursor-pointer' : ''
                }`}
              >
                <item.icon className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <div className="font-playfair text-xl font-bold text-slate-900">
                  {t(`campus.highlights.${item.key}.value`)}
                </div>
                <div className="font-inter text-slate-500 text-sm">
                  {t(`campus.highlights.${item.key}.label`)}
                </div>
                {item.url && (
                  <ExternalLink className="absolute top-2 right-2 w-3.5 h-3.5 text-slate-400" />
                )}
              </Tag>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]"
        >
          {campusImages.map((image, index) => {
            const isTrip = image.id === TRIP_ID;
            let ImgWrapper = 'div';
            let wrapperProps = {};
            if (isTrip) {
              ImgWrapper = 'button';
              wrapperProps = { type: 'button', onClick: openTrip };
            } else if (image.url_link) {
              ImgWrapper = 'a';
              wrapperProps = { href: image.url_link, target: '_blank', rel: 'noopener noreferrer' };
            }
            return (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                data-testid={`campus-image-${image.id}`}
                className={`relative rounded-2xl overflow-hidden group ${image.span}`}
              >
                <ImgWrapper
                  {...wrapperProps}
                  className="block w-full h-full text-left p-0 bg-transparent border-0 cursor-pointer"
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {image.badge && (
                    <span className="absolute top-3 left-3 bg-amber-500 text-slate-900 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md">
                      {image.badge}
                    </span>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                    <span className="font-inter text-sm">{image.alt}</span>
                    {(image.url_link || isTrip) && <ExternalLink className="w-3 h-3" />}
                  </div>
                </ImgWrapper>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Voyage mini-card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
          data-testid="travel-card"
          className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <a
            href="https://www.nice.aeroport.fr/"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-6 text-white hover:shadow-xl transition-all flex items-center gap-4"
            data-testid="travel-card-airport"
          >
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Plane className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-playfair text-lg font-bold mb-1">
                {t('campus.travel.airportTitle')}
              </h4>
              <p className="font-inter text-sm text-white/85 leading-snug">
                {t('campus.travel.airportDesc')}
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-white/70 group-hover:text-white" />
          </a>

          <button
            type="button"
            onClick={openTrip}
            className="group bg-white border border-slate-200 hover:border-amber-300 rounded-2xl p-6 transition-all flex items-center gap-4 text-left w-full"
            data-testid="travel-card-trips"
          >
            <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <MapPin className="w-7 h-7 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-playfair text-lg font-bold text-slate-900 mb-1">
                {t('campus.travel.tripsTitle')}
              </h4>
              <p className="font-inter text-sm text-slate-600 leading-snug">
                {t('campus.travel.tripsDesc')}
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-amber-600" />
          </button>

          <a
            href="https://provence-alpes-cotedazur.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-slate-900 hover:bg-slate-800 rounded-2xl p-6 text-white transition-all flex items-center gap-4"
            data-testid="travel-card-region"
          >
            <div className="w-14 h-14 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Palmtree className="w-7 h-7 text-slate-900" />
            </div>
            <div className="flex-1">
              <h4 className="font-playfair text-lg font-bold mb-1">
                {t('campus.travel.regionTitle')}
              </h4>
              <p className="font-inter text-sm text-slate-300 leading-snug">
                {t('campus.travel.regionDesc')}
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-amber-400" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-white rounded-3xl p-8 lg:p-10 border border-slate-200 shadow-sm"
          data-testid="environment-detail-card"
        >
          {/* Intro */}
          <div className="grid lg:grid-cols-5 gap-8 mb-10">
            <div className="lg:col-span-3">
              <h3 className="font-playfair text-2xl lg:text-3xl font-bold text-slate-900 mb-4 leading-tight">
                {t('campus.locationTitle')}
              </h3>
              <p className="font-inter text-slate-600 leading-relaxed">
                {t('campus.locationDesc')}
              </p>
            </div>
            <div className="lg:col-span-2 w-full h-48 rounded-xl overflow-hidden">
              <iframe
                title="Lafayette School Location"
                src="https://www.google.com/maps?q=10%20Avenue%20Georges%20Clemenceau%2006000%20Nice&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* 3 sub-sections — Interior takes 2 cols (denser content), Leisure + Safe stacked in last col for balance */}
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {[
              { id: 'interior', icon: Sparkles, color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', titleKey: 'interiorTitle', itemsKey: 'interiorItems', span: 'md:col-span-2 md:row-span-2' },
              { id: 'leisure', icon: Coffee, color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', titleKey: 'leisureTitle', itemsKey: 'leisureItems', span: '' },
              { id: 'safe', icon: Shield, color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', titleKey: 'safeTitle', itemsKey: 'safeItems', span: '' },
            ].map((sub) => {
              const items = t(`campus.environment.${sub.itemsKey}`, { returnObjects: true });
              const isInterior = sub.id === 'interior';
              return (
                <div
                  key={sub.id}
                  data-testid={`environment-sub-${sub.id}`}
                  className={`bg-slate-50 rounded-2xl p-6 border border-slate-100 ${sub.span}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${sub.color} flex items-center justify-center mb-4`}>
                    <sub.icon className="w-6 h-6" />
                  </div>
                  <h4 className="font-playfair text-lg font-bold text-slate-900 mb-4 leading-snug">
                    {t(`campus.environment.${sub.titleKey}`)}
                  </h4>
                  <ul className={`space-y-2.5 ${isInterior ? 'sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-2.5 sm:space-y-0' : ''}`}>
                    {(Array.isArray(items) ? items : []).map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-slate-700 text-sm leading-relaxed">
                        <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${sub.color.split(' ')[1]}`} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
      <TripModal open={tripOpen} onClose={() => setTripOpen(false)} />
    </section>
  );
};
